import React, { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { FiPlus, FiEdit2, FiTrash2, FiChevronDown, FiChevronUp, FiUpload, FiX } from 'react-icons/fi';
import axiosInstance from '../../api/axios';
import { formatCurrency } from '../../utils/currency';
import { localizeErrorDetail, getErrorDetail } from '../../utils/errorDetail';

const API_ORIGIN = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api').replace('/api', '');
const resolveImageUrl = (url: string | null | undefined): string =>
  !url ? '' : (url.startsWith('data:') || url.startsWith('http')) ? url : `${API_ORIGIN}${url}`;

// ─── Types ────────────────────────────────────────────────────────────────────

interface BYOOptionItem {
  id: number;
  category_id: number;
  name: string;
  name_zh: string | null;
  price_modifier: number;
  image_url: string | null;
  sort_order: number;
  active: boolean;
  updated_at: string | null;
}

interface BYOOptionCategory {
  id: number;
  name: string;
  name_zh: string | null;
  // Widened from 'single' | 'multi' (task-045): the backend column is an
  // unconstrained string with no enum/DB constraint, so a typo/legacy/future
  // third value must still type-check without a cast -- see SelectionTypeBadge's
  // three-way render branch below.
  selection_type: string;
  sort_order: number;
  active: boolean;
  items: BYOOptionItem[];
  updated_at: string | null;
}

// ─── API helpers ──────────────────────────────────────────────────────────────

const BASE = '/admin/byo-options';

const fetchBYOCategories = async (): Promise<BYOOptionCategory[]> => {
  const response = await axiosInstance.get<BYOOptionCategory[]>(`${BASE}/categories`);
  return response.data;
};

// ─── Styled Components (matching AdminBubbleTeaOptions pattern) ───────────────

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const Title = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #333;
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const IconButton = styled.button<{ $variant?: 'edit' | 'delete' }>`
  padding: 8px;
  background: ${props => props.$variant === 'delete' ? '#fee2e2' : '#ede9fe'};
  color: ${props => props.$variant === 'delete' ? '#dc2626' : '#667eea'};
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: ${props => props.$variant === 'delete' ? '#fecaca' : '#ddd6fe'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CancelButton = styled.button`
  padding: 10px 20px;
  background: #f3f4f6;
  color: #6b7280;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: #e5e7eb;
  }
`;

// ─── Image upload ─────────────────────────────────────────────────────────────

const ImageUploadArea = styled.div`
  border: 2px dashed #e5e7eb;
  border-radius: 8px;
  padding: 16px;
  text-align: center;
  margin-bottom: 8px;
`;

const FileInputHidden = styled.input`
  display: none;
`;

const UploadButtonLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: #f3f4f6;
  color: #6b7280;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;

  &:hover {
    background: #e5e7eb;
    color: #4b5563;
  }
`;

const ImagePreviewWrap = styled.div`
  position: relative;
  display: inline-block;
  margin-top: 8px;
`;

const PreviewImage = styled.img`
  width: 80px;
  height: 80px;
  object-fit: cover;
  border-radius: 8px;
  border: 2px solid #e5e7eb;
`;

const RemoveImageBtn = styled.button`
  position: absolute;
  top: -6px;
  right: -6px;
  background: rgba(0,0,0,0.6);
  color: white;
  border: none;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;

  &:hover {
    background: rgba(0,0,0,0.85);
  }
`;

// ─── Section (collapsible card) ───────────────────────────────────────────────

const Section = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;
  overflow: hidden;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 24px;
  cursor: pointer;
  user-select: none;
  transition: background 0.15s ease;

  &:hover {
    background: #f9fafb;
  }
`;

const SectionTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin: 0;
`;

const ItemCount = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  background: #f3f4f6;
  padding: 2px 8px;
  border-radius: 10px;
`;

// $selectionType widened to string (task-045) to accept any backend value,
// including a third/unknown one -- given its own distinct grey styling below,
// separate from the existing blue "Single" / amber "Multi" pair.
const SelectionTypeBadge = styled.span<{ $selectionType: string }>`
  display: inline-block;
  padding: 2px 8px;
  background: ${props =>
    props.$selectionType === 'single' ? '#dbeafe' :
    props.$selectionType === 'multi' ? '#fef3c7' :
    '#f3f4f6'};
  color: ${props =>
    props.$selectionType === 'single' ? '#1d4ed8' :
    props.$selectionType === 'multi' ? '#92400e' :
    '#6b7280'};
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
`;

const SectionHeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ChevronIcon = styled.span`
  color: #6b7280;
  display: flex;
  align-items: center;
`;

const SectionBody = styled.div<{ $isOpen: boolean }>`
  display: ${props => props.$isOpen ? 'block' : 'none'};
  border-top: 1px solid #e5e7eb;
`;

// ─── Table ────────────────────────────────────────────────────────────────────

const TableWrapper = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  padding: 12px 16px;
  text-align: left;
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
`;

const Td = styled.td`
  padding: 12px 16px;
  font-size: 14px;
  color: #333;
  border-bottom: 1px solid #f3f4f6;
  vertical-align: middle;
`;

const Tr = styled.tr`
  transition: background 0.1s ease;

  &:hover {
    background: #fafafa;
  }

  &:last-child td {
    border-bottom: none;
  }
`;

const ActionCell = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

// ─── Badges ───────────────────────────────────────────────────────────────────

const PriceBadge = styled.span`
  display: inline-block;
  padding: 3px 8px;
  background: #ede9fe;
  color: #5b21b6;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
`;

// ─── Toggle switch ────────────────────────────────────────────────────────────

const ToggleLabel = styled.label`
  position: relative;
  display: inline-block;
  width: 38px;
  height: 22px;
  cursor: pointer;

  input {
    opacity: 0;
    width: 0;
    height: 0;
  }

  span {
    position: absolute;
    inset: 0;
    background: #e5e7eb;
    border-radius: 22px;
    transition: background 0.2s ease;

    &::before {
      content: '';
      position: absolute;
      width: 16px;
      height: 16px;
      left: 3px;
      top: 3px;
      background: white;
      border-radius: 50%;
      transition: transform 0.2s ease;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
    }
  }

  input:checked + span {
    background: #667eea;

    &::before {
      transform: translateX(16px);
    }
  }

  input:disabled + span {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// ─── Empty state ──────────────────────────────────────────────────────────────

const EmptyRow = styled.tr``;

const EmptyCell = styled.td`
  padding: 32px 16px;
  text-align: center;
  color: #9ca3af;
  font-size: 14px;
`;

// ─── Modal ────────────────────────────────────────────────────────────────────

const Modal = styled.div<{ $isOpen: boolean }>`
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  align-items: center;
  justify-content: center;
  z-index: 2000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  max-width: 480px;
  width: 90%;
  padding: 24px;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const ModalTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: #333;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  color: #6b7280;
  cursor: pointer;
  line-height: 1;

  &:hover {
    color: #333;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

// Places an EN FormGroup next to its paired ZH (Chinese) FormGroup, e.g. for
// Name / Name (Chinese). Mirrors AdminInstantCakes.tsx / AdminQBMenu.tsx's
// FormRow pattern.
const FormRow = styled.div`
  display: flex;
  gap: 16px;

  ${FormGroup} {
    flex: 1;
    min-width: 0;
  }
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 6px;
`;

const Input = styled.input`
  padding: 8px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const Select = styled.select`
  padding: 8px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 14px;
  background: white;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #333;
  cursor: pointer;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
`;

const SubmitButton = styled(Button)`
  margin: 0;
`;

// ─── Loading / Error ──────────────────────────────────────────────────────────

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 400px;
`;

const Spinner = styled.div`
  border: 3px solid #e5e7eb;
  border-top: 3px solid #667eea;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div`
  background: #fee2e2;
  color: #991b1b;
  border-radius: 8px;
  padding: 16px 20px;
  font-size: 14px;
  margin-bottom: 20px;
`;


// task-035-impl-2: dismissible, page-level counterpart to ErrorMessage above
// for deleteCategoryMutation's onError -- mirrors AdminBubbleTeaOptions.tsx's
// ErrorBanner/ErrorDismissButton pair (same red ErrorMessage color scheme,
// flex layout with an inline dismiss button), but this file has no
// InfoMessage/InfoDismissButton to build on, so ErrorDismissButton is
// defined standalone here instead of extending one.
const ErrorBanner = styled(ErrorMessage)`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
`;

const ErrorDismissButton = styled.button`
  background: none;
  border: none;
  font-size: 18px;
  line-height: 1;
  color: #991b1b;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;

  &:hover {
    opacity: 0.7;
  }
`;

// ─── Item form state ──────────────────────────────────────────────────────────

interface ItemFormState {
  name: string;
  name_zh: string;
  price_modifier: number;
  image_url: string;
  sort_order: number;
  active: boolean;
  updated_at: string | null;
}

const BLANK_ITEM_FORM: ItemFormState = {
  name: '',
  name_zh: '',
  price_modifier: 0,
  image_url: '',
  sort_order: 0,
  active: true,
  updated_at: null,
};

// ─── Category form state ──────────────────────────────────────────────────────

interface CategoryFormState {
  name: string;
  name_zh: string;
  // Widened from 'single' | 'multi' (task-045) to stay consistent with
  // BYOOptionCategory.selection_type above -- editing a category whose
  // backend value is a third/unknown string must not force a cast/narrowing.
  selection_type: string;
  sort_order: number;
  active: boolean;
  updated_at: string | null;
}

const BLANK_CATEGORY_FORM: CategoryFormState = {
  name: '',
  name_zh: '',
  selection_type: 'single',
  sort_order: 0,
  active: true,
  updated_at: null,
};

// ─── Sub-component: one collapsible section ───────────────────────────────────

interface CategorySectionProps {
  category: BYOOptionCategory;
  onEditCategory?: (e: React.MouseEvent) => void;
  onDeleteCategory?: (e: React.MouseEvent) => void;
  deletingCategory?: boolean;
}

const CategorySection: React.FC<CategorySectionProps> = ({ category, onEditCategory, onDeleteCategory, deletingCategory }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(true);
  const [itemModalOpen, setItemModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<BYOOptionItem | null>(null);
  const [itemForm, setItemForm] = useState<ItemFormState>(BLANK_ITEM_FORM);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  // task-053-impl-2: per-CategorySection-instance state for deleteItemMutation/
  // toggleItemMutation's onError -- mirrors AdminBYOOptions's own top-level
  // deleteCategoryError pattern (see ErrorBanner render point below), but must
  // stay local here (not lifted to the parent component) since CategorySection
  // is instantiated once per category -- a shared/page-level state variable
  // would leak one category's item-action error into every other category's
  // section simultaneously.
  const [itemActionError, setItemActionError] = useState<string | null>(null);

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['byoOptionCategories'] });

  const createItemMutation = useMutation({
    mutationFn: (data: Partial<ItemFormState>) =>
      axiosInstance.post(`${BASE}/categories/${category.id}/items`, data).then(r => r.data),
    onSuccess: () => { setItemActionError(null); invalidate(); closeItemModal(); },
  });

  const updateItemMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<ItemFormState> }) =>
      axiosInstance.put(`${BASE}/items/${id}`, data).then(r => r.data),
    onSuccess: () => { setItemActionError(null); invalidate(); closeItemModal(); },
  });

  const deleteItemMutation = useMutation({
    mutationFn: (id: number) =>
      axiosInstance.delete(`${BASE}/items/${id}`).then(r => r.data),
    onSuccess: () => {
      setItemActionError(null);
      invalidate();
    },
    onError: (err: any) => {
      setItemActionError(localizeErrorDetail(getErrorDetail(err), t) || t('admin.byoOptions.itemDeleteError'));
    },
  });

  const toggleItemMutation = useMutation({
    mutationFn: (id: number) =>
      axiosInstance.patch(`${BASE}/items/${id}/toggle`).then(r => r.data),
    onSuccess: () => {
      setItemActionError(null);
      invalidate();
    },
    onError: (err: any) => {
      setItemActionError(localizeErrorDetail(getErrorDetail(err), t) || t('admin.byoOptions.itemToggleError'));
    },
  });

  const processFile = (file: File) => {
    if (!file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      setItemForm(prev => ({ ...prev, image_url: reader.result as string }));
    };
    reader.readAsDataURL(file);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
  };

  const openAddItem = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingItem(null);
    setItemForm({ ...BLANK_ITEM_FORM, sort_order: category.items.length });
    setItemModalOpen(true);
  };

  const openEditItem = (item: BYOOptionItem) => {
    setEditingItem(item);
    setItemForm({
      name: item.name,
      name_zh: item.name_zh ?? '',
      price_modifier: item.price_modifier,
      image_url: item.image_url ?? '',
      sort_order: item.sort_order,
      active: item.active,
      updated_at: item.updated_at ?? null,
    });
    setItemModalOpen(true);
  };

  const closeItemModal = () => {
    setItemModalOpen(false);
    setEditingItem(null);
    setItemForm(BLANK_ITEM_FORM);
    if (fileInputRef.current) fileInputRef.current.value = '';
    setUploadError(null);
  };

  const handleItemSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setUploadError(null);
    let imageUrl: string | null = itemForm.image_url.trim() || null;
    if (imageUrl && imageUrl.startsWith('data:')) {
      try {
        const res = await axiosInstance.post<{ url: string }>('/admin/upload-base64', { image: imageUrl });
        imageUrl = res.data.url;
      } catch {
        setUploadError(t('admin.byoOptions.imageUploadFailedError'));
        return;
      }
    }
    const payload: Record<string, unknown> = {
      ...itemForm,
      image_url: imageUrl,
      name_zh: itemForm.name_zh.trim() || null,
    };
    if (editingItem) {
      updateItemMutation.mutate({ id: editingItem.id, data: payload as Partial<ItemFormState> });
    } else {
      createItemMutation.mutate(payload as Partial<ItemFormState>);
    }
  };

  const handleDeleteItem = (id: number) => {
    if (window.confirm(t('admin.byoOptions.deleteItemConfirm'))) {
      setItemActionError(null);
      deleteItemMutation.mutate(id);
    }
  };

  const handleToggleItem = (id: number) => {
    setItemActionError(null);
    toggleItemMutation.mutate(id);
  };

  const isMutating = createItemMutation.isPending || updateItemMutation.isPending || !!deletingCategory;

  return (
    <>
      <Section data-testid={`byo-category-section-${category.id}`}>
        <SectionHeader onClick={() => setIsOpen(prev => !prev)}>
          <SectionTitleRow>
            <SectionTitle>{category.name}</SectionTitle>
            <SelectionTypeBadge $selectionType={category.selection_type}>
              {category.selection_type === 'single'
                ? t('admin.byoOptions.selectionTypeBadgeSingle')
                : category.selection_type === 'multi'
                ? t('admin.byoOptions.selectionTypeBadgeMulti')
                : t('admin.byoOptions.selectionTypeBadgeUnknown')}
            </SelectionTypeBadge>
            <ItemCount>{t(category.items.length === 1 ? 'admin.byoOptions.itemCountSingular' : 'admin.byoOptions.itemCountPlural', { count: category.items.length })}</ItemCount>
          </SectionTitleRow>
          <SectionHeaderRight>
            {onEditCategory && (
              <IconButton $variant="edit" title={t('admin.byoOptions.editCategoryTooltip')} disabled={!!deletingCategory} onClick={onEditCategory}>
                <FiEdit2 size={14} />
              </IconButton>
            )}
            {onDeleteCategory && (
              <IconButton $variant="delete" title={t('admin.byoOptions.deleteCategoryTooltip')} disabled={!!deletingCategory} onClick={onDeleteCategory}>
                <FiTrash2 size={14} />
              </IconButton>
            )}
            <Button data-testid="add-byo-item" disabled={!!deletingCategory} onClick={openAddItem}>
              <FiPlus size={14} />
              {t('admin.byoOptions.addItemButton')}
            </Button>
            <ChevronIcon>
              {isOpen ? <FiChevronUp size={18} /> : <FiChevronDown size={18} />}
            </ChevronIcon>
          </SectionHeaderRight>
        </SectionHeader>

        {itemActionError && (
          <ErrorBanner data-testid={`byo-item-action-error-${category.id}`}>
            <span>{itemActionError}</span>
            <ErrorDismissButton
              type="button"
              aria-label={t('admin.byoOptions.dismissNoticeLabel')}
              onClick={() => setItemActionError(null)}
            >
              ×
            </ErrorDismissButton>
          </ErrorBanner>
        )}

        <SectionBody $isOpen={isOpen}>
          <TableWrapper>
            <Table>
              <thead>
                <tr>
                  <Th>{t('admin.byoOptions.nameHeader')}</Th>
                  <Th>{t('admin.byoOptions.priceModifierHeader')}</Th>
                  <Th>{t('admin.byoOptions.imageUrlHeader')}</Th>
                  <Th>{t('admin.byoOptions.sortHeader')}</Th>
                  <Th>{t('admin.byoOptions.activeHeader')}</Th>
                  <Th>{t('admin.byoOptions.actionsHeader')}</Th>
                </tr>
              </thead>
              <tbody>
                {category.items.length === 0 ? (
                  <EmptyRow>
                    <EmptyCell colSpan={6}>
                      {t('admin.byoOptions.emptyItemsState')}
                    </EmptyCell>
                  </EmptyRow>
                ) : (
                  [...category.items]
                    .sort((a, b) => a.sort_order - b.sort_order)
                    .map(item => (
                      <Tr key={item.id}>
                        <Td>{item.name}</Td>
                        <Td>
                          {item.price_modifier > 0
                            ? <PriceBadge>+{formatCurrency(item.price_modifier)}</PriceBadge>
                            : <span style={{ color: '#9ca3af' }}>—</span>
                          }
                        </Td>
                        <Td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {item.image_url
                            ? <span style={{ color: '#667eea', fontSize: '12px' }}>{item.image_url}</span>
                            : <span style={{ color: '#9ca3af' }}>—</span>
                          }
                        </Td>
                        <Td style={{ color: '#6b7280' }}>{item.sort_order}</Td>
                        <Td>
                          <ToggleLabel title={item.active ? t('admin.byoOptions.deactivateTooltip') : t('admin.byoOptions.activateTooltip')}>
                            <input
                              type="checkbox"
                              checked={item.active}
                              disabled={toggleItemMutation.isPending || !!deletingCategory}
                              onChange={() => handleToggleItem(item.id)}
                            />
                            <span />
                          </ToggleLabel>
                        </Td>
                        <Td>
                          <ActionCell>
                            <IconButton
                              $variant="edit"
                              title={t('admin.byoOptions.editTooltip')}
                              disabled={!!deletingCategory}
                              onClick={() => openEditItem(item)}
                            >
                              <FiEdit2 size={14} />
                            </IconButton>
                            <IconButton
                              $variant="delete"
                              title={t('admin.byoOptions.deleteTooltip')}
                              disabled={deleteItemMutation.isPending || !!deletingCategory}
                              onClick={() => handleDeleteItem(item.id)}
                            >
                              <FiTrash2 size={14} />
                            </IconButton>
                          </ActionCell>
                        </Td>
                      </Tr>
                    ))
                )}
              </tbody>
            </Table>
          </TableWrapper>
        </SectionBody>
      </Section>

      {/* Add / Edit Item modal */}
      <Modal $isOpen={itemModalOpen}>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>
              {editingItem ? t('admin.byoOptions.modalEditItemTitle', { name: editingItem.name }) : t('admin.byoOptions.modalAddItemTitle', { categoryName: category.name })}
            </ModalTitle>
            <CloseButton onClick={closeItemModal}>×</CloseButton>
          </ModalHeader>

          <Form onSubmit={handleItemSubmit}>
            <FormRow>
              <FormGroup>
                <Label>{t('admin.byoOptions.nameLabel')}</Label>
                <Input
                  type="text"
                  value={itemForm.name}
                  onChange={e => setItemForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder={t('admin.byoOptions.namePlaceholder')}
                  required
                  autoFocus
                />
              </FormGroup>

              <FormGroup>
                <Label>{t('admin.byoOptions.nameZhLabel')}</Label>
                <Input
                  type="text"
                  value={itemForm.name_zh}
                  onChange={e => setItemForm(prev => ({ ...prev, name_zh: e.target.value }))}
                />
              </FormGroup>
            </FormRow>

            <FormGroup>
              <Label>{t('admin.byoOptions.priceModifierLabel')}</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={itemForm.price_modifier}
                onChange={e =>
                  setItemForm(prev => ({ ...prev, price_modifier: parseFloat(e.target.value) || 0 }))
                }
              />
            </FormGroup>

            <FormGroup>
              <Label>{t('admin.byoOptions.imageLabel')}</Label>
              <ImageUploadArea>
                <FileInputHidden
                  ref={fileInputRef}
                  type="file"
                  id="byoItemImageUpload"
                  accept="image/*"
                  onChange={handleFileChange}
                />
                <UploadButtonLabel htmlFor="byoItemImageUpload">
                  <FiUpload size={14} />
                  {t('admin.byoOptions.uploadImageButton')}
                </UploadButtonLabel>
              </ImageUploadArea>
              {itemForm.image_url && (
                <ImagePreviewWrap>
                  <PreviewImage src={resolveImageUrl(itemForm.image_url)} alt={t('admin.imagePreviewAlt')} />
                  <RemoveImageBtn
                    type="button"
                    onClick={() => setItemForm(prev => ({ ...prev, image_url: '' }))}
                  >
                    <FiX size={10} />
                  </RemoveImageBtn>
                </ImagePreviewWrap>
              )}
              <Input
                type="text"
                value={itemForm.image_url.startsWith('data:') ? t('admin.byoOptions.imageSelectedPlaceholder') : itemForm.image_url}
                readOnly={itemForm.image_url.startsWith('data:')}
                onChange={e => {
                  if (!itemForm.image_url.startsWith('data:')) {
                    setItemForm(prev => ({ ...prev, image_url: e.target.value }));
                  }
                }}
                placeholder={t('admin.byoOptions.imageUrlPlaceholder')}
                style={{ marginTop: 8 }}
              />
            </FormGroup>

            <FormGroup>
              <Label>{t('admin.byoOptions.sortOrderLabel')}</Label>
              <Input
                type="number"
                min="0"
                value={itemForm.sort_order}
                onChange={e =>
                  setItemForm(prev => ({ ...prev, sort_order: parseInt(e.target.value) || 0 }))
                }
              />
            </FormGroup>

            <FormGroup>
              <CheckboxLabel>
                <input
                  type="checkbox"
                  checked={itemForm.active}
                  onChange={e => setItemForm(prev => ({ ...prev, active: e.target.checked }))}
                />
                {t('admin.byoOptions.activeCheckboxLabel')}
              </CheckboxLabel>
            </FormGroup>

            {uploadError && <ErrorMessage>{uploadError}</ErrorMessage>}
            {(createItemMutation.isError || updateItemMutation.isError) && (
              <ErrorMessage>
                {localizeErrorDetail(getErrorDetail(createItemMutation.error || updateItemMutation.error), t) ||
                  t('admin.byoOptions.genericError')}
              </ErrorMessage>
            )}

            <ModalActions>
              <CancelButton type="button" onClick={closeItemModal}>
                {t('admin.byoOptions.cancelButton')}
              </CancelButton>
              <SubmitButton type="submit" disabled={isMutating}>
                {isMutating
                  ? (editingItem ? t('admin.byoOptions.savingButton') : t('admin.byoOptions.addingButton'))
                  : (editingItem ? t('admin.byoOptions.saveChangesButton') : t('admin.byoOptions.addSubmitItemButton'))}
              </SubmitButton>
            </ModalActions>
          </Form>
        </ModalContent>
      </Modal>
    </>
  );
};

// ─── Main page component ──────────────────────────────────────────────────────

const AdminBYOOptions: React.FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<BYOOptionCategory | null>(null);
  const [categoryForm, setCategoryForm] = useState<CategoryFormState>(BLANK_CATEGORY_FORM);
  // task-035-impl-2: previously deleteCategoryMutation had no onError handler
  // at all, so a failed category delete failed silently. See ErrorBanner
  // render point below (near <Header>) for the dismissible banner this drives.
  const [deleteCategoryError, setDeleteCategoryError] = useState<string | null>(null);
  // task-036-fix-3: tracks which category id is currently being deleted so
  // deletingCategory gating (passed to CategorySection below) only disables
  // the section actually being deleted, not every category on the page.
  const [deletingCategoryId, setDeletingCategoryId] = useState<number | null>(null);

  const { data: rawCategories, isLoading, isError } = useQuery({
    queryKey: ['byoOptionCategories'],
    queryFn: fetchBYOCategories,
  });
  const categories = Array.isArray(rawCategories) ? rawCategories : [];

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['byoOptionCategories'] });

  const createCategoryMutation = useMutation({
    mutationFn: (data: CategoryFormState) =>
      axiosInstance.post(`${BASE}/categories`, data).then(r => r.data),
    onSuccess: () => { invalidate(); closeCategoryModal(); },
  });

  const updateCategoryMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<CategoryFormState> }) =>
      axiosInstance.put(`${BASE}/categories/${id}`, data).then(r => r.data),
    onSuccess: () => { invalidate(); closeCategoryModal(); },
  });

  const deleteCategoryMutation = useMutation({
    mutationFn: (id: number) =>
      axiosInstance.delete(`${BASE}/categories/${id}`).then(r => r.data),
    onSuccess: () => {
      setDeleteCategoryError(null);
      invalidate();
    },
    onError: (err: any) => {
      setDeleteCategoryError(localizeErrorDetail(getErrorDetail(err), t) || t('admin.byoOptions.categoryDeleteError'));
    },
    onSettled: () => setDeletingCategoryId(null),
  });

  const openAddCategory = () => {
    setEditingCategory(null);
    setCategoryForm({ ...BLANK_CATEGORY_FORM, sort_order: categories.length });
    setCategoryModalOpen(true);
  };

  const openEditCategory = (e: React.MouseEvent, cat: BYOOptionCategory) => {
    e.stopPropagation();
    setEditingCategory(cat);
    setCategoryForm({
      name: cat.name,
      name_zh: cat.name_zh ?? '',
      selection_type: cat.selection_type,
      sort_order: cat.sort_order,
      active: cat.active,
      updated_at: cat.updated_at ?? null,
    });
    setCategoryModalOpen(true);
  };

  const closeCategoryModal = () => {
    setCategoryModalOpen(false);
    setEditingCategory(null);
    setCategoryForm(BLANK_CATEGORY_FORM);
  };

  const handleCategorySubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const payload: Record<string, unknown> = {
      ...categoryForm,
      name_zh: categoryForm.name_zh.trim() || null,
    };
    if (editingCategory) {
      updateCategoryMutation.mutate({ id: editingCategory.id, data: payload as Partial<CategoryFormState> });
    } else {
      createCategoryMutation.mutate(payload as unknown as CategoryFormState);
    }
  };

  const handleDeleteCategory = (e: React.MouseEvent, id: number) => {
    e.stopPropagation();
    if (window.confirm(t('admin.byoOptions.deleteCategoryConfirm'))) {
      setDeleteCategoryError(null);
      setDeletingCategoryId(id);
      deleteCategoryMutation.mutate(id);
    }
  };

  const isCategoryMutating = createCategoryMutation.isPending || updateCategoryMutation.isPending;

  if (isLoading) {
    return (
      <LoadingContainer>
        <Spinner />
      </LoadingContainer>
    );
  }

  if (isError) {
    return (
      <Container>
        <Header>
          <Title>{t('admin.byoOptions.title')}</Title>
        </Header>
        <ErrorMessage>{t('admin.byoOptions.loadError')}</ErrorMessage>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>{t('admin.byoOptions.title')}</Title>
        <Button data-testid="add-byo-category" onClick={openAddCategory}>
          <FiPlus size={14} />
          {t('admin.byoOptions.addCategoryButton')}
        </Button>
      </Header>

      {deleteCategoryError && (
        <ErrorBanner>
          <span>{deleteCategoryError}</span>
          <ErrorDismissButton
            type="button"
            aria-label={t('admin.byoOptions.dismissNoticeLabel')}
            onClick={() => setDeleteCategoryError(null)}
          >
            ×
          </ErrorDismissButton>
        </ErrorBanner>
      )}

      {[...categories]
        .sort((a, b) => a.sort_order - b.sort_order)
        .map(category => (
          <CategorySection
            key={category.id}
            category={category}
            onEditCategory={e => openEditCategory(e, category)}
            onDeleteCategory={e => handleDeleteCategory(e, category.id)}
            deletingCategory={deletingCategoryId === category.id}
          />
        ))}

      {categories.length === 0 && (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: '#9ca3af', background: 'white', borderRadius: '12px' }}>
          <p>{t('admin.byoOptions.emptyCategoriesState')}</p>
        </div>
      )}

      {/* Add / Edit Category modal */}
      <Modal $isOpen={categoryModalOpen}>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>
              {editingCategory ? t('admin.byoOptions.modalEditCategoryTitle', { name: editingCategory.name }) : t('admin.byoOptions.modalAddCategoryTitle')}
            </ModalTitle>
            <CloseButton onClick={closeCategoryModal}>×</CloseButton>
          </ModalHeader>

          <Form onSubmit={handleCategorySubmit}>
            <FormRow>
              <FormGroup>
                <Label>{t('admin.byoOptions.categoryNameLabel')}</Label>
                <Input
                  type="text"
                  value={categoryForm.name}
                  onChange={e => setCategoryForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder={t('admin.byoOptions.categoryNamePlaceholder')}
                  required
                  autoFocus
                />
              </FormGroup>

              <FormGroup>
                <Label>{t('admin.byoOptions.categoryNameZhLabel')}</Label>
                <Input
                  type="text"
                  value={categoryForm.name_zh}
                  onChange={e => setCategoryForm(prev => ({ ...prev, name_zh: e.target.value }))}
                />
              </FormGroup>
            </FormRow>

            <FormGroup>
              <Label>{t('admin.byoOptions.selectionTypeLabel')}</Label>
              <Select
                value={categoryForm.selection_type}
                onChange={e =>
                  setCategoryForm(prev => ({ ...prev, selection_type: e.target.value }))
                }
              >
                <option value="single">{t('admin.byoOptions.selectionTypeSingleOption')}</option>
                <option value="multi">{t('admin.byoOptions.selectionTypeMultiOption')}</option>
              </Select>
            </FormGroup>

            <FormGroup>
              <Label>{t('admin.byoOptions.sortOrderLabel')}</Label>
              <Input
                type="number"
                min="0"
                value={categoryForm.sort_order}
                onChange={e =>
                  setCategoryForm(prev => ({ ...prev, sort_order: parseInt(e.target.value) || 0 }))
                }
              />
            </FormGroup>

            <FormGroup>
              <CheckboxLabel>
                <input
                  type="checkbox"
                  checked={categoryForm.active}
                  onChange={e => setCategoryForm(prev => ({ ...prev, active: e.target.checked }))}
                />
                {t('admin.byoOptions.activeCheckboxLabel')}
              </CheckboxLabel>
            </FormGroup>

            {(createCategoryMutation.isError || updateCategoryMutation.isError) && (
              <ErrorMessage>
                {localizeErrorDetail(getErrorDetail(createCategoryMutation.error || updateCategoryMutation.error), t) ||
                  t('admin.byoOptions.genericError')}
              </ErrorMessage>
            )}

            <ModalActions>
              <CancelButton type="button" onClick={closeCategoryModal}>
                {t('admin.byoOptions.cancelButton')}
              </CancelButton>
              <SubmitButton type="submit" disabled={isCategoryMutating}>
                {isCategoryMutating
                  ? (editingCategory ? t('admin.byoOptions.savingButton') : t('admin.byoOptions.addingButton'))
                  : (editingCategory ? t('admin.byoOptions.saveChangesButton') : t('admin.byoOptions.addSubmitCategoryButton'))}
              </SubmitButton>
            </ModalActions>
          </Form>
        </ModalContent>
      </Modal>
    </Container>
  );
};

export default AdminBYOOptions;
